import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/AuthContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import VerifyEmailPendingPage from "@/pages/VerifyEmailPendingPage";
import AdminAuthSettingsPage from "@/pages/AdminAuthSettingsPage";
import AdminPromptInjectionPage from "@/pages/AdminPromptInjectionPage";
import AdminSecurityScorePage from "@/pages/AdminSecurityScorePage";
import AdminSecopContractsPage from "@/pages/AdminSecopContractsPage";
import AdminSecopContractDetailPage from "@/pages/AdminSecopContractDetailPage";
import AdminSecopConfigPage from "@/pages/AdminSecopConfigPage";
import AdminSecopHealthPage from "@/pages/AdminSecopHealthPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import UserDashboard from "@/pages/UserDashboard";
import ProfileSecurityPage from "@/pages/ProfileSecurityPage";
import NewCasePage from "@/pages/NewCasePage";
import CaseDetailPage from "@/pages/CaseDetailPage";
import CaseChecklistPage from "@/pages/CaseChecklistPage";
import EditDraftPage from "@/pages/EditDraftPage";
import PrevalidationPage from "@/pages/PrevalidationPage";
import PaymentPage from "@/pages/PaymentPage";
import PaymentN2Page from "@/pages/PaymentN2Page";
import Nivel2RequestPage from "@/pages/Nivel2RequestPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCaseSecopPage from "@/pages/AdminCaseSecopPage";
import AdminCaseDiagnosisPage from "@/pages/AdminCaseDiagnosisPage";
import AdminCaseKitDocumentalPage from "@/pages/AdminCaseKitDocumentalPage";
import AdminCaseUnifiedPage from "@/pages/AdminCaseUnifiedPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminCasesListPage from "@/pages/AdminCasesListPage";
import AdminPaymentsPage from "@/pages/AdminPaymentsPage";
import AdminReviewsPage from "@/pages/AdminReviewsPage";
import AdminRequestsPage from "@/pages/AdminRequestsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminCostsPage from "@/pages/AdminCostsPage";
import AdminAuditPage from "@/pages/AdminAuditPage";
import AdminNpsPage from "@/pages/AdminNpsPage";
import AdminLandingConfigPage from "@/pages/AdminLandingConfigPage";
import AdminEmailsPage from "@/pages/AdminEmailsPage";
import AdminEmailSchedulerPage from "@/pages/AdminEmailSchedulerPage";
import SequenceRunsPage from "@/pages/SequenceRunsPage";
import EmailDesignerPage from "@/pages/EmailDesignerPage";
import AdminMaintenancePage from "@/pages/AdminMaintenancePage";
import AdminPromptsPage from "@/pages/AdminPromptsPage";
import AdminAIRunsPage from "@/pages/AdminAIRunsPage";
import AdminAIStatsPage from "@/pages/AdminAIStatsPage";
import AdminInfraAuditPage from "@/pages/AdminInfraAuditPage";
import AdminNormativaReviewPage from "@/pages/AdminNormativaReviewPage";
import AdminPromptEditorPage from "@/pages/AdminPromptEditorPage";
import AdminPromptHistoryPage from "@/pages/AdminPromptHistoryPage";
import AdminPromptsAbPage from "@/pages/AdminPromptsAbPage";
import AdminLayout from "@/components/AdminLayout";
import ReviewerDashboard from "@/pages/ReviewerDashboard";
import ReviewerCaseDetailPage from "@/pages/ReviewerCaseDetailPage";
import ReviewerKitDocumentalPage from "@/pages/ReviewerKitDocumentalPage";
import { TermsPage, DataPolicyPage, NotFoundPage } from "@/pages/StaticPages";
import NormativaPage from "@/pages/NormativaPage";

// Fase 11.5 code-quality fix #13: role constants extracted to avoid
// creating new array references on every render in ProtectedRoute props.
const ROLES_ADMIN = ["admin"];
const ROLES_USER = ["user"];
const ROLES_REVIEWER = ["reviewer"];
const ROLES_ANY_AUTH = ["user", "admin", "reviewer"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Admin routes use AdminLayout (sidebar + its own header) — NOT wrapped by public <Layout> */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={ROLES_ADMIN}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="cases" element={<AdminCasesListPage />} />
            <Route path="cases/:id" element={<AdminCaseUnifiedPage />} />
            <Route path="cases/:id/secop" element={<AdminCaseSecopPage />} />
            <Route path="cases/:id/diagnosis" element={<AdminCaseDiagnosisPage />} />
            <Route path="cases/:id/kit-documental" element={<AdminCaseKitDocumentalPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="reviews" element={<AdminReviewsPage />} />
            <Route path="requests" element={<AdminRequestsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="costs" element={<AdminCostsPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="nps" element={<AdminNpsPage />} />
            <Route path="landing" element={<AdminLandingConfigPage />} />
            <Route path="emails" element={<AdminEmailsPage />} />
            <Route path="emails/scheduler" element={<AdminEmailSchedulerPage />} />
            <Route path="emails/sequences/:id/runs" element={<SequenceRunsPage />} />
            <Route path="emails/templates/preview/:templateId" element={<EmailDesignerPage />} />
            <Route path="maintenance" element={<AdminMaintenancePage />} />
            <Route path="prompts" element={<AdminPromptsPage />} />
            <Route path="prompts/ab-tests" element={<AdminPromptsAbPage />} />
            <Route path="prompts/:promptKey" element={<AdminPromptEditorPage />} />
            <Route path="prompts/:promptKey/history" element={<AdminPromptHistoryPage />} />
            <Route path="ai-runs" element={<AdminAIRunsPage />} />
            <Route path="ai-runs/:runId" element={<AdminAIRunsPage />} />
            <Route path="ai-stats" element={<AdminAIStatsPage />} />
            <Route path="infra-audit" element={<AdminInfraAuditPage />} />
            <Route path="normativa-review" element={<AdminNormativaReviewPage />} />
            <Route path="auth-settings" element={<AdminAuthSettingsPage />} />
            <Route path="security/prompt-injection" element={<AdminPromptInjectionPage />} />
            <Route path="security/score" element={<AdminSecurityScorePage />} />
            <Route path="secop/contracts" element={<AdminSecopContractsPage />} />
            <Route path="secop/contracts/:contractKey" element={<AdminSecopContractDetailPage />} />
            <Route path="secop/health" element={<AdminSecopHealthPage />} />
            <Route path="secop/config" element={<AdminSecopConfigPage />} />
          </Route>

          {/* Public + authenticated user/reviewer routes use the public Layout (header + footer) */}
          <Route element={<Layout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/verify-email-pending" element={<VerifyEmailPendingPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terminos" element={<TermsPage />} />
            <Route path="/politica-datos" element={<DataPolicyPage />} />
            <Route path="/normativa-aplicable" element={<NormativaPage />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={ROLES_USER}>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/security"
              element={
                <ProtectedRoute allowedRoles={ROLES_ANY_AUTH}>
                  <ProfileSecurityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/new"
              element={
                <ProtectedRoute allowedRoles={ROLES_USER}>
                  <NewCasePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/:id"
              element={
                <ProtectedRoute allowedRoles={ROLES_ANY_AUTH}>
                  <CaseDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/:id/checklist"
              element={
                <ProtectedRoute allowedRoles={ROLES_ANY_AUTH}>
                  <CaseChecklistPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/:id/prevalidation"
              element={
                <ProtectedRoute allowedRoles={ROLES_ANY_AUTH}>
                  <PrevalidationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/:id/edit-draft"
              element={
                <ProtectedRoute allowedRoles={ROLES_USER}>
                  <EditDraftPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/:id/payment"
              element={
                <ProtectedRoute allowedRoles={ROLES_USER}>
                  <PaymentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/:id/nivel2-request"
              element={
                <ProtectedRoute allowedRoles={ROLES_USER}>
                  <Nivel2RequestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cases/:id/payment-n2"
              element={
                <ProtectedRoute allowedRoles={ROLES_USER}>
                  <PaymentN2Page />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviewer/cases/:id/diagnosis"
              element={
                <ProtectedRoute allowedRoles={ROLES_REVIEWER}>
                  <AdminCaseDiagnosisPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviewer"
              element={
                <ProtectedRoute allowedRoles={ROLES_REVIEWER}>
                  <ReviewerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviewer/cases/:id"
              element={
                <ProtectedRoute allowedRoles={ROLES_REVIEWER}>
                  <ReviewerCaseDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviewer/cases/:id/kit-documental"
              element={
                <ProtectedRoute allowedRoles={ROLES_REVIEWER}>
                  <ReviewerKitDocumentalPage />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
